import { Model } from '@/models/Model';
import { Address } from '@/models/Address';
import { Answer } from '@/models/Answer';
import { Contractor } from '@/models/Contractor';
import { Task } from '@/models/Task';
import { User } from '@/models/User';

export class Report extends Model {
  protected $name = 'Report';

  protected $endpoint = '/hin/hin-reports';

  protected $primaryKey = 'uuid';

  protected $fillable: string[] = ['status_raw', 'instructions'];

  public uuid?: string;

  public case_number?: string;

  public img_case_number?: string;

  public status_raw?: string;

  public answers?: Answer[];

  public tasks?: Task[];

  public media?: Media[];

  public applicant?: User;

  public intermediary?: User;

  public contractor?: Contractor;

  public contract_links?: Record<string, string>;

  public first_appointment?: string;

  public original_first_appointment?: string;

  public instructions?: string;

  public delivery_appointment?: string;

  public original_delivery_appointment?: string;

  public first_contact?: string;

  public assessment_conversation?: string;

  public address?: Address;

  public has_witnessed?: boolean;

  public custom_applicant_email?: string;

  public custom_applicant_phone?: string;

  public custom_intermediary_name?: string;

  public custom_intermediary_phone?: string;

  public custom_intermediary_email?: string;

  public getContract(): Promise<any> {
    const url = `${this.$endpoint}/${this.uuid}/contractor-assignment`;

    return this.request('get', url);
  }

  public signContract(): Promise<any> {
    const url = `${this.$endpoint}/${this.uuid}/sign-contract`;

    return this.request('post', url);
  }

  public uploadEvaluation(payload: any): Promise<any> {
    const url = `${this.$endpoint}/${this.uuid}/upload-evaluation`;

    return this.request('post', url, payload);
  }

  public createWordFile(): Promise<any> {
    const url = `${this.$endpoint}/${this.uuid}/contract?type=.docx`;

    return this.request('get', url);
  }

  public mediaEndpoint(id: string) {
    this.$endpoint = `reports/${id}/media`;

    return this;
  }

  public importableReportsEndpoint() {
    this.$endpoint = '/hin/list-importable-reports/';

    return this;
  }

  public importReport(report_id: string): Promise<any> {
    const url = `/hin/copy-report/${report_id}`;

    return this.request('get', url, {}, '0');
  }

  public listMedia(payload: any): Promise<any> {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/media`;

    return this.request('get', url, payload, '0');
  }

  public addMedia(payload: AttachmentPayload): Promise<void> {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/media`;

    return this.request('post', url, payload, '0');
  }

  public deleteMediaEndpoint(id: string): Promise<void> {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/media/${id}`;

    return this.request('delete', url);
  }

  public updateMediaEndpoint(id:string, attributes?: Record<string, unknown>): Promise<void> {
    const url = `${this.$endpoint}${this.resolvePrimaryKey().length ? `/${this.resolvePrimaryKey()}` : ''}/media/${id}`;

    return this.request('patch', url, this.getPayload(attributes));
  }

  public createv2(): Promise<void> {
    const url = `${this.$endpoint}${this.resolvePrimaryKey().length ? `/${this.resolvePrimaryKey()}` : ''}/create-v2`;

    return this.request('post', url);
  }

  public getApplicantPhone(): string | null | undefined
  {
    if (this.custom_applicant_phone) {
      return this.custom_applicant_phone;
    }

    if (! this.applicant) {
      return null;
    }

    return this.applicant.phone ? this.applicant.phone : this.applicant.mobile;
  }

  public getApplicantEmail(): string | null | undefined
  {
    if (this.custom_applicant_phone) {
      return this.custom_applicant_email;
    }

    return this.applicant ? this.applicant.email : null;
  }

  public getIntermediaryName(): string | null | undefined
  {
    if (this.custom_intermediary_name) {
      return this.custom_intermediary_name;
    }

    return this.intermediary ? this.intermediary.name : null;
  }

  public getIntermediaryPhone(): string | null | undefined
  {
    if (this.custom_intermediary_phone) {
      return this.custom_intermediary_phone;
    }

    return this.intermediary ? this.intermediary.phone : null;
  }

  public getIntermediaryEmail(): string | null | undefined
  {
    if (this.custom_intermediary_email) {
      return this.custom_intermediary_email;
    }

    return this.intermediary ? this.intermediary.email : null;
  }
}

export interface Media {
  description?: string;
  file_name?: string;
  mime_type?: string;
  original?: string;
  hin_show_to_all?: boolean | null;
  sort_order?: string | number;
  thumb?: string;
  get_url?: string;
  type?: string;
  uuid?: string;
  created_at?: string;
  uploaded_by?: string;
  uploaded_on?: string;
  kpi_points?: boolean | null;
  amount_of_kpi_points?: number | null;
}
export interface AttachmentPayload {
  file?: string;
  file_name?: string;
  description?: string | undefined;
  hin_show_to_all?: boolean | undefined;
  type?: string | undefined;
  kpi_points?: boolean | undefined;
  amount_of_kpi_points?: number | undefined;
}
